import { get, set } from 'lodash-es';
import { IFormatDataParams } from '@home/utils/dbDataFormat/_type';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';

function filterTrendDataProportional(data: any[], labelIndices: number[], sampleRatio = 0.2) {
  const getNum = (val: string) => parseFloat(val || "0");
  const keepIndices = new Set<number>();

  // Step 1: 保留所有 label 的 index
  labelIndices.forEach(i => {
    if (i >= 0 && i < data.length) {
      keepIndices.add(i);
    }
  });

  // Step 2: 按区间做采样
  for (let k = 0;k < labelIndices.length - 1;k++) {
    const start = labelIndices[k];
    const end = labelIndices[k + 1];

    const tempKeep: number[] = [];

    for (let i = start + 1;i < end;i++) {
      const item = data[i];
      const hasCurrentSales = getNum(item.values?.current_sales) > 0;
      if (hasCurrentSales) {
        keepIndices.add(i); // 一定保留
      } else {
        tempKeep.push(i); // 等待采样
      }
    }

    // 保留部分无用点做平滑（按比例）
    const keepCount = Math.ceil(tempKeep.length * sampleRatio);
    const step = Math.max(1, Math.floor(tempKeep.length / keepCount));

    for (let i = 0;i < tempKeep.length;i += step) {
      keepIndices.add(tempKeep[i]);
    }
  }

  // Step 3: 返回保留后的数据
  return data.filter((_, index) => keepIndices.has(index));
}

// Forecast 面积趋势图数据处理，保留关键节点，对中间的点进行线性插值
export function trendFormatData(data: any[]) {
  const labelIndices = [0, 29, 89, 179, 364]; // 对应 1,30,90,180,365 天
  const filteredData = filterTrendDataProportional(data, labelIndices, 0.3); // 可调比例

  const filledData = [...filteredData];
  const getNum = (val: string) => parseFloat(val || "0");
  let lastLabelIndex = -1;

  for (let i = 0;i < filledData.length;i++) {
    const current = filledData[i];
    // 如果当前数据点有 label，说明是一个关键点（可视点或锚点）
    if (current.label) {
    // 如果之前已经遇到过一个带 label 的点，且中间有间隔（即不是连续的 label）
      if (lastLabelIndex !== -1 && lastLabelIndex < i - 1) {
      // 起点（上一个有 label 的点）
        const start = filledData[lastLabelIndex]; 
        // 终点（当前有 label 的点）
        const end = current;    
        // 起点和终点之间的距离（包含起点和终点之间的点数）                
        const span = i - lastLabelIndex;         
        // 读取起点和终点的预测区间的上/下限值
        const startUpper = getNum(start.values.upper_forecast);
        const endUpper = getNum(end.values.upper_forecast);
        const startLower = getNum(start.values.lower_forecast);
        const endLower = getNum(end.values.lower_forecast);
        // 对中间的点进行线性插值
        for (let j = 1;j < span;j++) {
          // 当前插值目标点
          const target = filledData[lastLabelIndex + j]; 

          // 如果目标点没有 label（只对无 label 的点插值）
          if (!target.label) {
          // 对 upper_forecast 做线性插值
            target.values.upper_forecast = (
              (startUpper * (span - j) + endUpper * j) / span
            ).toFixed(6);
            // 对 lower_forecast 做线性插值
            target.values.lower_forecast = (
              (startLower * (span - j) + endLower * j) / span
            ).toFixed(6);
          }
        }
      }
      // 更新上一个有 label 的点的索引
      lastLabelIndex = i;
    }
  }

  return {
    list: filledData.map((item) => {
      const metrics: Record<string, { value: string; }> = {};
      Object.entries(item.values || {}).forEach(([key, value]) => {
        metrics[key] = { value };
      });

      return {
        date: item.date,
        label: item.label,
        metrics,
      };
    }),
  };
}


export interface IFormatRule {
  /** 数据的 key 路径（支持点号表示法，如 'list.info.date'）如需要数组索引，请使用 'list[0].info.date' 格式 */
  key: string;
  
  /** 
   * 格式化规则
   * 
   * 1. 基础类型（返回对应的原始类型）:
   *    - 'float': 浮点数类型 (number)
   *    - 'string': 字符串类型 (string)
   * 
   * 2. 数值格式化类型
   *    详见: @home/utils/dbDataFormat/dataFormat.ts
   */
  format:
  // 基础类型
  | 'float' | 'string'
  // dataFormat - @home/utils/dbDataFormat/dataFormat.ts 支持的所有格式化规则
  | IFormatDataParams['formatRule'];

  expectedOutputFormat?: 'string' | 'number';
  
  /**
   * 是否保留原始值
   * 如果为 true，会创建一个 _raw_${key} 字段保存原始值
   * 例如：value: "123.456" → value: 123.46, _raw_value: "123.456"
   * 默认: false
   */
  keepRawValue?: boolean;
}

export interface IFormatRules extends Omit<IFormatRule, 'key'> {
  keys: string[];
}

interface ICompiledRule extends IFormatRule {
  normalizedKey?: string;
  pathSegments?: string[];
}

/**
 * 格式化单个值
 * @param value - 要格式化的值
 * @param rule - 格式化规则
 * @returns 格式化后的值
 */
const formatValue = (value: any, rule: IFormatRule): any => {
  if (value === null || value === undefined) {
    return value;
  }

  const { format, expectedOutputFormat = 'number' } = rule || {};

  if (!format) {
    return value;
  }

  try {
    // 基础类型转换
    if (format === 'float') {
      return parseFloat(String(value));
    }

    if (format === 'string') {
      return String(value);
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      return value;
    }

    const formatted = dataFormat({ value, formatRule: format });
    if (expectedOutputFormat === 'number') {
      const numFormatted =  Number(formatted);
      if (!isNaN(numFormatted)) {
        return numFormatted;
      }
    }
    if (typeof formatted === expectedOutputFormat) {
      return formatted;
    }

    // 默认返回原值
    return value;
  } catch (error) {
    console.error(error);
    return value;
  }
};

/**
 * @param data - 要格式化的数据（会被直接修改）
 * @param rules - 已编译的格式化规则
 */
const applyRulesToData = (data: any, rules: ICompiledRule[]): void => {
  if (!data || typeof data !== 'object') {
    return;
  }

  rules.forEach(rule => {
    const { key, pathSegments, keepRawValue } = rule;
    
    // 处理可能包含数组的路径，支持两种语法：
    // 1. 'list.items.name' - 自动应用到数组所有元素
    // 2. 'list[0].name' - 只应用到指定索引
    
    if (key.includes('[')) {
      // 支持数组索引语法（如 'list[0].name'），使用 lodash 的 get/set 可以直接处理这种路径
      const value = get(data, key);
      if (value !== undefined) {
        if (keepRawValue) {
          // 提取最后一个路径段作为 key 名
          const lastDotIndex = key.lastIndexOf('.');
          const lastBracketIndex = key.lastIndexOf('[');
          const lastPathSeparatorIndex = Math.max(lastDotIndex, lastBracketIndex);
          
          if (lastPathSeparatorIndex > 0) {
            const parentPath = key.substring(0, lastPathSeparatorIndex);
            const fieldName = key.substring(lastDotIndex + 1);
            const rawKey = lastDotIndex > lastBracketIndex 
              ? `${parentPath}._raw_${fieldName}`
              : key.replace(/\[(\d+)\]$/, '[_raw_$1]');
            
            set(data, rawKey, value);
          }
        }
        
        const formatted = formatValue(value, rule);
        set(data, key, formatted);
      }
    } else {
      // 不包含索引，需要递归处理所有数组元素
      applyRuleRecursively(data, pathSegments || [], rule, 0);
    }
  });
};

/**
 * 递归应用格式化到路径
 * 自动处理数组
 */
const applyRuleRecursively = (
  data: any,
  pathSegments: string[],
  rule: IFormatRule,
  segmentIndex: number
): void => {
  if (segmentIndex >= pathSegments.length) {
    return;
  }

  const segment = pathSegments[segmentIndex];
  const isLastSegment = segmentIndex === pathSegments.length - 1;

  // 如果当前数据不存在，直接返回
  if (data === null || typeof data !== 'object') {
    return;
  }

  // 处理数组
  if (Array.isArray(data)) {
    data.forEach(item => {
      applyRuleRecursively(item, pathSegments, rule, segmentIndex);
    });
    return;
  }

  // 处理对象
  const value = data[segment];
  
  if (isLastSegment) {
    // 到达最后一个段，应用格式化
    if (value !== undefined) {
      // 如果需要保留原始值
      if (rule.keepRawValue) {
        const rawKey = `_raw_${segment}`;
        // eslint-disable-next-line no-param-reassign
        data[rawKey] = value;
      }
      
      // 应用格式化
      // eslint-disable-next-line no-param-reassign
      data[segment] = formatValue(value, rule);
    }
  } else {
    // 继续递归
    if (value !== undefined) {
      applyRuleRecursively(value, pathSegments, rule, segmentIndex + 1);
    }
  }
};

/**
 * 根据规则格式化数据（会直接修改原始数据）
 * @param data - 要处理的数据（会被直接修改）
 * @param formatRules - 格式化规则数组
 * @returns 处理后的数据
 * 
 * @example
 * ```ts
 * // 示例 1: 使用数组索引
 * const data = {
 *   items: [
 *     { price: '123.456' },
 *     { price: '789.012' }
 *   ]
 * };
 * 
 * formatDataByRules(data, [
 *   { key: 'items[0].price', format: 'decimals', decimal: 2 }
 * ]);
 * // 只格式化第一个元素：
 * // { items: [{ price: 123.46 }, { price: '789.012' }] }
 * 
 * // 示例 2: 应用到所有数组元素
 * formatDataByRules(data, [
 *   { key: 'items.price', format: 'decimals', decimal: 2 }
 * ]);
 * // 格式化所有元素：
 * // { items: [{ price: 123.46 }, { price: 789.01 }] }
 * ```
 */
export function formatDataByRules<T = any>(
  data: T,
  formatRules?: IFormatRule[],
): any {
  if (!data) {
    return data;
  }
  
  try {
    if (formatRules && formatRules.length > 0) {
      // 预编译规则
      const compiledRules = formatRules.map(rule => {
        const compiled: ICompiledRule = { ...rule };
        compiled.normalizedKey = rule.key.replace(/\[\d+\]/g, '');
        compiled.pathSegments = compiled.normalizedKey.split('.');
        return compiled;
      });
      
      applyRulesToData(data, compiledRules);
    }
    
    return data;
  } catch (error) {
    console.error(error);
    return data;
  }
}

// ==================== 主指标显示值 & KMBT 缩写气泡 ====================

/**
 * KMBT 缩写匹配正则
 *
 * @description
 * 仅当字符串中存在「数字 + K/M/B/T」形如的尾缀时，才视为缩写值。
 * 收敛边界，避免误判：
 *   - 业务名称 / 英文单位（如 'MS'、'KEY'）开头的字母
 *   - 单纯字符串中的 K/M/B/T 字面量（如 'NaN'、'-'）
 *
 * @example
 *   '1.2K'   → match
 *   '345.6M' → match
 *   '-'      → no match
 *   'KEY'    → no match
 */
const KMBT_ABBR_REGEX = /\d[KMBT]\b/i;

/**
 * 主指标值是否被视为「无效值」（用于跳过格式化与气泡渲染）
 *
 * @param value     原始值
 * @param formatted 格式化后的展示值
 */
const isInvalidMetricValue = (
  value: string | number | null | undefined,
  formatted: string | number | null | undefined,
): boolean => {
  return (
    value === '-'
    || value === null
    || value === undefined
    || value === ''
    || formatted === '-'
  );
};

/**
 * 主指标值显示解析的输入参数
 */
export interface IResolveMainValueDisplayParams {
  /** 原始数值（可能是 number / string / '-' / null / undefined） */
  value: string | number | null | undefined;
  /** 模板中携带的动态变量上下文，用于 `dynamicString` 渲染 formatRule 模板 */
  data?: Record<string, unknown> | unknown[];
  /**
   * 基础格式化展示值（已经过 formatRule 或 fallback 处理）
   *
   * @description
   * 由调用方计算后传入，作为「不缩写」时的展示值，
   * 同时用于 KMBT 缩写气泡的「精确数值」回显。
   *
   * @example
   *   formatter ? dataFormat({ value, formatRule: dynamicString(formatter, data) }) : fallback
   */
  baseFormattedValue: string | number | null | undefined;
  /**
   * 主指标专用格式化规则（可选）
   *
   * @description
   * 后端 / 七彩石可下发该字段，用于将大数据自动缩写为 KMBT；
   * 支持 `dynamicString` 模板，如 `'kmb_${unit}'`。
   * 当未配置时，主展示值与 `baseFormattedValue` 保持一致，且不会出现气泡。
   */
  mainValueFormatRule?: string;
}

/**
 * 主指标值显示解析的输出结果
 */
export interface IResolveMainValueDisplayResult {
  /** 主展示值：若 `mainValueFormatRule` 命中则为缩写后值，否则等同 `baseFormattedValue` */
  mainDisplayValue: string | number | null | undefined;
  /**
   * 主展示值的「精确气泡内容」
   *
   * @description
   * - 仅当 `mainValueFormatRule` 命中、且主展示值是 KMBT 缩写、且原值有效时返回 `baseFormattedValue`
   * - 否则返回 `null`，调用方可据此判断是否渲染 `<CommonBubble>`
   */
  mainValueBubble: string | number | null | undefined;
  /** 已经过 `dynamicString` 解析后的主指标格式化规则；未配置时为 `null` */
  resolvedMainValueFormatRule: string | null;
}

/**
 * 解析主指标的展示值与 KMBT 缩写气泡内容
 *
 * @description
 * 抽离自 `MetricCard` / `SimpleMetricGrid` 的重复逻辑，统一处理：
 *   1. 通过 `dynamicString` 渲染主指标格式化规则模板
 *   2. 调用 `dataFormat` 得到主展示值
 *   3. 当主展示值为 KMBT 缩写时，回填精确值供气泡展示
 *   4. 对无效值（'-' / null / undefined / ''）跳过气泡渲染
 *
 * @param params 输入参数，详见 {@link IResolveMainValueDisplayParams}
 * @returns 解析结果，详见 {@link IResolveMainValueDisplayResult}
 *
 * @example
 * ```ts
 * const baseFormattedValue = formatter
 *   ? dataFormat({ value, formatRule: dynamicString(formatter, data) })
 *   : getDecimalFormateStr(type, value, metrics_name, decimal);
 *
 * const { mainDisplayValue, mainValueBubble } = resolveMainValueDisplay({
 *   value,
 *   data,
 *   baseFormattedValue,
 *   mainValueFormatRule,
 * });
 * ```
 */
export const resolveMainValueDisplay = (
  params: IResolveMainValueDisplayParams,
): IResolveMainValueDisplayResult => {
  const { value, data, baseFormattedValue, mainValueFormatRule } = params || {};

  // 未配置主指标格式化规则：直接复用 baseFormattedValue，不渲染气泡
  if (!mainValueFormatRule) {
    return {
      mainDisplayValue: baseFormattedValue,
      mainValueBubble: null,
      resolvedMainValueFormatRule: null,
    };
  }

  const resolvedMainValueFormatRule = dynamicString(mainValueFormatRule, data);
  // 原值无效（null / undefined / '-' / ''）时退化为 baseFormattedValue，
  // 避免 dataFormat 对 KMBT 缩写规则产生不可控输出
  const mainDisplayValue = isInvalidMetricValue(value as string | number | null | undefined, baseFormattedValue)
    ? baseFormattedValue
    : dataFormat({
      value: value as string | number,
      formatRule: resolvedMainValueFormatRule,
    });

  // 仅当：原值有效 + 缩写值与基础值不同 + 缩写值匹配 KMBT 后缀，才返回气泡
  const isAbbreviated =
    !isInvalidMetricValue(value, baseFormattedValue)
    && String(mainDisplayValue) !== String(baseFormattedValue)
    && KMBT_ABBR_REGEX.test(String(mainDisplayValue));

  return {
    mainDisplayValue,
    mainValueBubble: isAbbreviated ? baseFormattedValue : null,
    resolvedMainValueFormatRule,
  };
};
